import { createAnnotation, insertText } from 'substance'
import { idGenerator } from '../lib/utils/IdGenerator'
import { insertTextNode } from './document/insertTextNode'
import { insertBlockNode } from './document/insertBlockNode'
import {Event} from '../lib/constants/Event'

/**
 * Document manipulation methods
 *
 * @class
 * @alias api.document
 */
class Document {
    /** @hideconstructor */
    constructor(api) {
        this.api = api
    }

    /**
     * Insert a text node in specified place of the document using an insert
     * strategy based on the options object. The options object and all properties
     * are optional.
     * Return the created node only if using an existing transaction (tx).
     *
     * - tx: Optional TransactionDocument
     * - mode: Insert node where: first, last, before or after
     * - refNode: If insert mode is before or after a reference node is mandatory
     * - text: Optional text content
     * - type: Text style type, ie paragraph, preamble, etc
     *
     * @example
     * // Insert new empty paragraph at cursor position (or last if no cursor in text)
     * import {api} from 'writer'
     * api.document.insertTextNode()
     *
     * @example
     * // Insert a preamble with content after the first node in the document as part of your own transaction
     * import {api} from 'writer'
     * const firstNode = api.document.nodes()
     * api.editorSession.transaction(tx => {
     *   api.document.insert({
     *     tx: tx,
     *     type: 'preamble',
     *     text: 'My new preamble',
     *     mode: 'after',
     *     refNode: nodes[0] // First node
     *   })
     * })
     *
     * @param {{tx: TransactionDocument, type: string, mode: string, text: string, refNode: object }} options Optional object with optional properties
     * @return {object|undefined}
     */
    insertTextNode({ tx = null, type = 'paragraph', text = '', mode = null, refNode = null } = {}) {
        if (tx) {
            return insertTextNode(tx, type, text, mode, refNode)
        }
        else {
            this.api.editorSession.transaction(tx => {
                insertTextNode(tx, type, text, mode, refNode)
            })
        }
    }

    /**
     * Insert a block node in specified place of the document using an insert strategy
     * based on the options object. The data property that defines the node is mandatory.
     * All other properties are optional. (To insert a node the node type must be defined
     * by a plugin and known to the Writer.)
     * Return the created node only if using an existing transaction (tx).
     *
     * - data: Mandatory object describing the node, may or may not have an id
     * - tx: Optional TransactionDocument
     * - mode: Insert node where: first, last, before or after
     * - refNode: If insert mode is before or after a reference node is mandatory
     *
     * @example
     * // Insert a new node of type "myquote"
     * import {api} from 'writer'
     *
     * api.document.insertBlockNode({
     *   data: {
     *     type: 'myquote',
     *     content: 'People who deny the existence of dragons are often eaten by dragons'
     *     attribution: 'Ursula K. Le Guin'
     *   }
     * })
     *
     * @example
     * // Insert a new node after the first node in the document as part of your own transaction
     * import {api} from 'writer'
     *
     * const firstNode = api.document.nodes()
     * api.editorSession.transaction(tx => {
     *   api.document.insert({
     *     tx: tx,
     *     data: {
     *       type: 'myquote',
     *       content: 'People who deny the existence of dragons are often eaten by dragons'
     *       attribution: 'Ursula K. Le Guin'
     *     },
     *     mode: 'after',
     *     refNode: nodes[0] // First node
     *   })
     * })
     *
     * @param {{tx: TransactionDocument, data: object, mode: string, refNode: object}|string} options Optional object with optional properties
     * @return {object|undefined}
     */
    insertBlockNode(options, deprecatedData) {
        if (typeof deprecatedData !== 'undefined') {
            console.warn('Using api.document.insertBlockNode() with [name] and [data] parameters is deprecated! Please see API documentation on usage!')
            return this._deprecatedInsertBlockNode(options, deprecatedData)
        }

        const { tx = null, data, mode = null, refNode = null } = options || {}

        if (tx) {
            return insertBlockNode(tx, data, mode, refNode)
        }
        else {
            this.api.editorSession.transaction(tx => {
                insertBlockNode(tx, data, mode, refNode)
            })
        }
    }

    /**
     * Fetch all nodes in the document, optionally filtered by type
     *
     * @example
     * // Fetch all image nodes in the document
     * import {api} from 'writer'
     *
     * const nodes = api.document.nodes('ximimage')
     *
     * @param {string|null} filter Optional filter returned nodes by type
     * @return {array}
     */
    nodes(filter = null) {
        const doc = this.api.editorSession.getDocument()
        const ids = doc.getNodes()['body'].nodes
        const nodes = ids.map(id => doc.get(id))

        if (filter !== null) {
            return nodes.filter(node => node.type === filter)
        }

        return nodes
    }

    /**
     * Triggers a fetch resource from a nodes url property. For example when
     * node has properties that is only referenced through an external url.
     *
     * @param {BlockNode} node The resource node
     * @param {Object} info Object with information to created substance transaction
     *
     * @example
     * import {api} from 'writer'
     *
     * // Fetch oauth from Instagram, don't save this fetch in undo/redo history
     * api.document.triggerFetchResourceNode(this.props.node, { history: false })
     */
    triggerFetchResourceNode(node, info) {
        this.api.resourceManager.triggerFetch(node, info)
    }

    /**
     * Insert an inline node at current selection
     * @param {string} name The plugin which inserts inline node
     * @param {object} data Data defined by node schema
     * @returns {*}
     */
    insertInlineNode(name, data) {
        const surface = this.api.editorSession.getFocusedSurface();
        if (!surface) {
            throw new Error("Trying to insert node with no active surface");
        }
        return surface.transaction((tx, args) => {
            // 1. Insert fake character where the citation should stick on
            args = insertText(tx, {
                selection: args.selection,
                text: '$'
            });
            var citationSel = this.api.editorSession.createSelection({
                type: 'property',
                path: args.selection.path,
                startOffset: args.selection.endOffset - 1,
                endOffset: args.selection.endOffset
            });

            // 2. Create citation annotation
            args.annotationType = name;
            args.annotationData = data;
            args.selection = citationSel;
            args.splitContainerSelections = false;
            args.containerId = surface.getContainerId();

            args = createAnnotation(tx, args);
            return args;
        });
    }


    /**
     * Function to insert block node at current selection
     * @private
     * @deprecated 2018-12-04
     *
     * @param {string} name Plugin that's inserting block node
     * @param {object} data Data defined by node
     */
    _deprecatedInsertBlockNode(name, data) {
        let editorSession = this.api.editorSession;
        let surface = editorSession.getFocusedSurface();
        let result;

        if (!surface) {
            throw new Error("Trying to insert node with no active surface");
        }

        // Add type and a generated id if not provided
        data.type = !data.type ? name : data.type;
        data.id = !data.id ? idGenerator() : data.id;

        editorSession.transaction((tx, args) => {
            // args.node = data;
            // args.containerId = 'body';
            // result = insertNode(tx, args);
            tx.insertBlockNode(data)
            // NOTE: need to return result here, so that the selection is set
            // return result
        })

        return result
    }


    /**
     * Retrieve the previous node.
     * Uses the focused surface to get all nodes in that surface/container and
     * then returns the previous node from the one sent in
     * @param {string} nodeId
     * @returns {*}
     */
    getPreviousNode(nodeId) {
        const editorSession = this.api.editorSession;
        const surface = editorSession.surfaceManager.getFocusedSurface()
        const doc = editorSession.getDocument()
        const surfaceNode = doc.get(surface.id)
        const surfaceNodes = surfaceNode.nodes
        const currentNodeIndex = surfaceNodes.indexOf(nodeId)
        const previousNodeId = surfaceNodes[currentNodeIndex - 1]

        if (currentNodeIndex === 0 || currentNodeIndex === -1) {
            return undefined
        }
        return doc.get(previousNodeId)
    }

    /**
     * Deletes a node from the document.
     * Triggers a 'document:changed' event to all document:changed listeners except
     * the plugin making the change.
     *
     * @param {string} name Plugin name
     * @param {object} node Node to delete, must contain an id
     *
     * @example
     * import {api} from 'writer'
     * api.deleteNode(this.props.node);
     *
     * @fires document:changed
     */
    deleteNode(name, node) {
        // TODO: is this actually always a node in the body?
        // i.e. the surface is always the body editor?
        const editorSession = this.api.editorSession;

        // Select previous node before we delete the node
        // So we dont get any issues with SwitchTextTypeTool
        editorSession.selectNode(node.id)


        editorSession.transaction((tx) => {
            const sel = tx.selection

            const nodeId = sel.getNodeId()
            const container = tx.get(sel.containerId)
            const nodePos = container.getPosition(nodeId)
            const contentPath = container.getContentPath()
            tx.update(contentPath, {delete: {offset: nodePos}})
            tx.delete(nodeId)
            const newNode = tx.createDefaultTextNode()
            tx.update(contentPath, {type: 'insert', pos: nodePos, value: newNode.id})
            tx.selection = tx.createSelection({
                type: 'property',
                path: newNode.getTextPath(),
                startOffset: 0,
                containerId: container.id,
            })


        })

        const event = {
            type: 'document',
            action: 'delete',
            data: node.id
        }
        this.api.events.trigger(name, Event.DOCUMENT_CHANGED, event)
    }

    /**
     * Get all nodes in the document
     * @deprecated 2018-12-05
     *
     * @returns {Array}
     */
    getDocumentNodes() {
        console.warn('api.document.getDocumentNodes() is deprecated, use api.document.nodes() instead')
        return this.nodes()
    }
}

export {Document}
